import identity from "lodash/identity"
import range from "lodash/range"
import sortBy from "lodash/sortBy"
import React, { useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router"
import { match } from "ts-pattern"

import { cn } from "@/lib/utils"
import { useXl2RenderQuery } from "@/src/graphql/admin-generated-rq"
import { IconMathFunction } from "@tabler/icons-react"

import { Loader } from "./Loader"
import s from "./XL.module.scss"

export interface Workbook {
  sheetNames: string[]
  ranges: NamedRange[]
  sheets: Sheet[]
}

export interface Sheet {
  name: string
  cells: Cell[]
  maxCol: string
  maxRow: number
  fonts: Lookup<Font>
  borders: Lookup<Border[]>
  fills: Lookup<Fill>
  showGrid: boolean
  widths: Record<number, number>
  emptyLines: [number, number][]
}

interface Cell {
  col: string
  row: number
  value: string
  formula?: string | null
}

interface NamedRange {
  name: string
  sheetName: string
  ref: string
}

interface Lookup<T> {
  /* RecordId -> T */
  lookup: T[]
  /* rows: RowNum -> [ColNum, RecordId, ColNum, RecordId) */
  sheets: SheetStyle[]
}

interface SheetStyle {
  sheet_name: string
  rows: Record<number, number[]>
}

interface Font {
  bold: boolean
  italic: boolean
  strike: boolean
  underline: boolean
  color: string | null
}

interface Border {
  loc: string
  style: string
  color: string | null
}

interface Fill {
  style: string
  color: string
}

export function XLIndex() {
  const result = useXl2RenderQuery(undefined, { refetchInterval: 5000 })
  return (
    <Loader
      result={result}
      render={({ data }) => <Render workbook={JSON.parse(data.xl2)} />}
    />
  )
}

function Render({ workbook }: { workbook: Workbook }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const querySheet = searchParams.get("sheet") ?? ""

  const sheetName =
    typeof querySheet == "string" && workbook.sheetNames.includes(querySheet)
      ? querySheet
      : workbook.sheets.at(0)?.name
  const [showFormulas, setShowFormulas] = useState(false)

  const sheet = workbook.sheets.find((s) => s.name == sheetName)

  if (sheet) {
    // Hardcode a wider label column til we get actual widths from styles.
    sheet.widths[1] = 40
  }

  return (
    <div className="grid h-screen grid-rows-[auto_minmax(0,1fr)_auto]">
      <div className="p-1 text-[12px]">
        <ul className="flex items-baseline gap-4">
          <li>
            <label>
              <input
                type="checkbox"
                checked={showFormulas}
                onChange={() => setShowFormulas(!showFormulas)}
              />{" "}
              Show formulas
            </label>
          </li>
          <select
            value={sheetName}
            className="rounded bg-accent-1-200 p-1"
            onChange={(e) =>
              setSearchParams((params) => {
                params.set("sheet", e.target.value)
                return params
              })
            }
          >
            {workbook.sheetNames.sort().map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <li></li>
        </ul>
      </div>
      {sheet && (
        <RenderSheet
          className="w-screen"
          key={sheet.name}
          sheet={sheet}
          showFormulas={showFormulas}
        />
      )}
      <div
        style={{ scrollbarWidth: "thin" }}
        className="sticky bottom-0 overflow-scroll border-t border-t-grey-300 bg-white"
      >
        <ul className="pl-4 text-[12px] whitespace-nowrap">
          {sortBy(workbook.sheetNames, identity).map((s, key) => (
            <li
              className={cn(
                "inline-block p-2 pt-3 text-grey-400 hover:cursor-pointer hover:border-b-4 hover:bg-accent-3-100 hover:text-accent-3-600",
                s == sheetName && "border-b-4 bg-accent-1-200 text-accent-1-600",
              )}
              key={key}
            >
              <Link
                to={{
                  pathname: "/admin/xlsx",
                  search: new URLSearchParams({
                    sheet: s,
                  }).toString(),
                }}
              >
                {s}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function renderCell(cell: Cell, showFormulas: boolean) {
  if (showFormulas) {
    return cell.formula || cell.value
  } else {
    return cell.value
  }
}

export function RenderSheet({
  sheet,
  showFormulas,
  className,
}: {
  sheet: Sheet
  showFormulas: boolean
  className?: string
}) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const [selectedCell, setSelectedCell] = useState<{
    cell: Cell
    rowIdx: number
    colIdx: number
  }>()
  const xs: Record<number, Record<string, Cell>> = {}
  sheet.cells.forEach((cell) => {
    if (!xs[cell.row]) {
      xs[cell.row] = {}
    }
    xs[cell.row][cell.col] = cell
  })
  const cells = rows(sheet.maxRow + 5).map((row) =>
    cols(sheet.maxCol).map((col) => xs[row] && xs[row][col]),
  )
  const styles = prepStyles(sheet)
  const empty = Object.fromEntries(sheet.emptyLines)

  useEffect(() => {
    function scrollIntoView(row: number, col: number) {
      const el = document.getElementById(`cell-${row}-${col}`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      function clamp(n: number, min: number, max: number) {
        return Math.min(Math.max(n, min), max)
      }

      function selectCell(rowIdx: number, colIdx: number) {
        event.preventDefault()
        setSelectedCell({
          cell: cells[rowIdx][colIdx],
          rowIdx,
          colIdx,
        })
        scrollIntoView(rowIdx, colIdx)
      }

      if (selectedCell) {
        let { rowIdx, colIdx } = selectedCell
        const maxRow = cells.length
        const maxCol = cells[0].length

        match(event.key)
          .with("ArrowUp", () => {
            rowIdx = clamp(rowIdx - 1, 0, maxRow - 1)
            selectCell(rowIdx, colIdx)
          })
          .with("ArrowDown", () => {
            rowIdx = clamp(rowIdx + 1, 0, maxRow - 1)
            selectCell(rowIdx, colIdx)
          })
          .with("ArrowLeft", () => {
            colIdx = clamp(colIdx - 1, 0, maxCol - 1)
            selectCell(rowIdx, colIdx)
          })
          .with("ArrowRight", () => {
            colIdx = clamp(colIdx + 1, 0, maxCol - 1)
            selectCell(rowIdx, colIdx)
          })
          .otherwise(() => {})
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [cells, selectedCell])

  return (
    <div className={cn(className, "grid grid-rows-[auto_minmax(0,1fr)]")}>
      <div className="sticky left-0 flex items-center gap-2 border border-grey-300 p-1 text-body1">
        <IconMathFunction className="text-colors-text-secondary" size={22} />
        <span className="text-body1">
          {selectedCell?.cell?.formula || selectedCell?.cell?.value}
        </span>
      </div>
      <div className="overflow-auto">
        <div ref={sheetRef} className={cn(s.cells, { [s.showGrid]: sheet.showGrid })}>
          <div className={s.colgroup}>
            <div className={s.col} style={{ width: 45 }} />
            {cols(sheet.maxCol).map((c, idx) => (
              <div
                className={s.col}
                key={c}
                style={{ width: sheet.widths[idx + 1] * 7 || 100 }}
              />
            ))}
          </div>
          <div className={s.thead}>
            <div className={s.tr}>
              <div className={s.th}></div>
              {cols(sheet.maxCol).map((c) => (
                <div className={s.th} key={c}>
                  {c}
                </div>
              ))}
            </div>
          </div>
          <div className={s.tbody}>
            {cells.map((row, rowIdx) => (
              <React.Fragment key={rowIdx}>
                <div className={s.tr}>
                  <div className={cn(s.th, s.rowNumbers)}>{rowIdx + 1}</div>
                  {row.map((cell, colIdx) => {
                    const font = (styles.fonts[rowIdx + 1] ?? {})[colIdx + 1]
                    const fill = (styles.fills[rowIdx + 1] ?? {})[colIdx + 1]
                    const border = (styles.borders[rowIdx + 1] ?? {})[colIdx + 1]
                    return (
                      <div
                        key={colIdx}
                        id={`cell-${rowIdx}-${colIdx}`}
                        className={cn(s.td, "cursor-pointer truncate", {
                          "outline outline-colors-border-active":
                            selectedCell &&
                            colIdx == selectedCell.colIdx &&
                            rowIdx == selectedCell.rowIdx,
                        })}
                        onClick={() => {
                          if (
                            selectedCell &&
                            colIdx == selectedCell.colIdx &&
                            rowIdx == selectedCell.rowIdx
                          ) {
                            setSelectedCell(undefined)
                          } else {
                            setSelectedCell({ cell, colIdx, rowIdx })
                          }
                        }}
                        style={{ ...font, ...fill, ...border }}
                        title={cell && renderCell(cell, showFormulas)}
                      >
                        {cell && renderCell(cell, showFormulas)}
                      </div>
                    )
                  })}
                </div>
                {Array.from(Array(empty[rowIdx + 1] || 0).keys()).map((_, idx) => (
                  <div className={s.tr} key={"empty" + rowIdx + idx}>
                    <div className={cn(s.th, s.rowNumbers)}>&nbsp;</div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function cols(col: string): string[] {
  const A = "A".charCodeAt(0)
  const end = col
    .split("")
    .reverse()
    .map((c, idx) => (c.charCodeAt(0) - A + 1) * Math.pow(26, idx))
    .reduce((a, b) => a + b)
  return range(1, 1 + end + 1).map(col_str)
}

function col_str(num: number): string {
  const A = "A".charCodeAt(0)
  if (num < 27) {
    return String.fromCharCode(A + num - 1)
  } else {
    return col_str(num / 27) + String.fromCharCode(A + (num % 27))
  }
}

function rows(maxRow: number): number[] {
  return range(1, maxRow + 1)
}

// RowNum -> ColNum -> style
type R = Record<number, Record<number, Record<string, string>>>
function prepStyles(sheet: Sheet): { fonts: R; fills: R; borders: R } {
  const fonts = sheet.fonts.lookup.map((f) => {
    const rv: Record<string, string> = {}
    if (f.bold) {
      rv.fontWeight = "bold"
    }
    if (f.italic) {
      rv.fontStyle = "italic"
    }
    if (f.strike) {
      rv.textDecoration = "line-through"
    }
    if (f.underline) {
      rv.textDecoration = "underline"
    }
    if (f.color) {
      rv.color = "#" + f.color
    }
    return rv
  })
  const fills = sheet.fills.lookup.map((f) => ({ backgroundColor: "#" + f.color }))
  const borders = sheet.borders.lookup.map((bs) =>
    Object.fromEntries(
      bs.map((b) => {
        const loc = b.loc[0].toUpperCase() + b.loc.slice(1)
        return ["border" + loc, `1px solid #${b.color}`]
      }),
    ),
  )
  return {
    fonts: unroll(fonts, find(sheet.name, sheet.fonts)),
    fills: unroll(fills, find(sheet.name, sheet.fills)),
    borders: fixBorders(sheet, unroll(borders, find(sheet.name, sheet.borders))),
  }
}

function find<T>(name: string, xs: Lookup<T>): SheetStyle | undefined {
  name = name.toUpperCase()
  return xs.sheets.find((s) => s.sheet_name.toUpperCase() == name)
}

function unroll<T>(
  lookup: T[],
  ss: SheetStyle | undefined,
): Record<number, Record<number, T>> {
  if (!ss) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(ss.rows).map(([rowNum, cells]) => {
      const rv: Record<number, T> = {}
      for (let i = 0; i < cells.length; i += 2) {
        rv[cells[i]] = lookup[cells[i + 1]]
      }
      return [rowNum, rv]
    }),
  )
}

// When two adjacent cells have borders, the borders of the cell to the top and
// to the right take precedence. Thus, if we want to display a borderTop or a
// borderLeft we must disable the border of the adjacent cell. The adjacent cell
// will have a border if we are showing gridlines.
// https://www.w3.org/TR/CSS2/tables.html#border-conflict-resolution
function fixBorders(sheet: Sheet, xs: R) {
  if (sheet.showGrid) {
    Object.entries(xs).forEach(([_rowIdx, cells]) => {
      Object.entries(cells).forEach(([_colIdx, bs]) => {
        const rowIdx = +_rowIdx,
          colIdx = +_colIdx
        if (rowIdx == 0 || colIdx == 0) {
          return
        }
        if ("borderTop" in bs) {
          if (!xs[rowIdx - 1]) xs[rowIdx - 1] = {}
          if (!xs[rowIdx - 1][colIdx]) xs[rowIdx - 1][colIdx] = {}
          xs[rowIdx - 1][colIdx]["borderBottom"] = "none"
        }
        if ("borderLeft" in bs) {
          if (!xs[rowIdx]) xs[rowIdx] = {}
          if (!xs[rowIdx][colIdx - 1]) xs[rowIdx][colIdx - 1] = {}
          xs[rowIdx][colIdx - 1]["borderRight"] = "none"
        }
        if ("borderLeft" in bs && colIdx == 1) {
          bs["borderLeft"] = "none"
        }
      })
    })
  }
  return xs
}
